/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { ChevronDownIcon } from "lucide-react"
import type { ComponentProps, FunctionComponent } from "react"
import { useId } from "react"

import AndroidIcon from "../../assets/images/android.svg?react"
import AppleIcon from "../../assets/images/apple.svg?react"
import { applicationTypePlatforms } from "../../features/application-type/application-type-schema"
import type { ApplicationTypePlatform } from "../../features/application-type/application-type-types"
import { Button } from "../primitives/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger
} from "../primitives/dropdown-menu"

/**
 * SVG brand mark imported with `?react`, compatible with `currentColor`.
 */
type BrandMark = FunctionComponent<ComponentProps<"svg">>

/**
 * Label and brand mark for each supported platform.
 *
 * `satisfies` ensures every platform has an entry. The menu order follows
 * `applicationTypePlatforms`.
 */
const PlatformOption = {
    ANDROID: { label: "Android", Icon: AndroidIcon },
    APPLE: { label: "Apple", Icon: AppleIcon }
} as const satisfies Record<ApplicationTypePlatform, { label: string; Icon: BrandMark }>

interface Props {
    /**
     * ID of the trigger button, referenced by its label.
     */
    id: string
    /**
     * ID of the label announced before the selected platform.
     */
    labelId: string
    /**
     * Currently selected platform, or `undefined` if none is selected.
     */
    value: ApplicationTypePlatform | undefined
    /**
     * Called when the user selects a platform.
     */
    onChange: (platform: ApplicationTypePlatform) => void
}

/**
 * Lets the user select an application's platform from a menu of brand-marked options.
 *
 * Uses a radio group to expose the selected platform's checked state to assistive
 * technology and show a checkmark in the menu.
 */
export const PlatformDropdown = ({ id, labelId, value, onChange }: Props) => {
    const valueId = useId()

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                id={id}
                aria-labelledby={`${labelId} ${valueId}`}
                render={<Button variant="outline" className="h-10 w-full justify-between font-normal" />}
            >
                {value && (
                    <PlatformLabel id={valueId} label={PlatformOption[value].label} Icon={PlatformOption[value].Icon} />
                )}
                <ChevronDownIcon />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
                {/* Base UI types the value as `any`; every item carries a platform. */}
                <DropdownMenuRadioGroup
                    value={value}
                    onValueChange={(platform: ApplicationTypePlatform) => onChange(platform)}
                >
                    {applicationTypePlatforms.map((platform) => (
                        // Close the menu after selection; the default radio-item behavior keeps it open.
                        <DropdownMenuRadioItem key={platform} value={platform} closeOnClick>
                            <PlatformLabel
                                label={PlatformOption[platform].label}
                                Icon={PlatformOption[platform].Icon}
                            />
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

interface PlatformLabelProps {
    /**
     * Optional ID for the trigger's `aria-labelledby`.
     * Only the trigger label needs an ID.
     */
    id?: string
    /**
     * Visible platform name, e.g. "Android".
     */
    label: string
    /**
     * SVG brand mark for the platform.
     */
    Icon: BrandMark
}

/**
 * Renders a platform's brand mark and name.
 *
 * The mark is decorative, so it is hidden from screen readers; the text
 * provides the platform name.
 */
const PlatformLabel = ({ id, label, Icon }: PlatformLabelProps) => (
    <span id={id} className="flex items-center gap-2">
        <Icon aria-hidden="true" />
        {label}
    </span>
)
